import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  AppSwitcher,
  createAgionAppSwitcherApps,
  getCurrentAgionAppId,
  type AgionAppId,
} from '@agion/ui'
import { isPlatformAdmin } from '@agion/auth'
import { getConfig } from '@/core/analytics_runtime_env'

interface SessionResponse {
  authenticated?: boolean
  platformAdmin?: boolean
  role?: string
}

interface AnalyticsAppSwitcherProps {
  collapsed?: boolean
}

export const AnalyticsAppSwitcher: React.FC<AnalyticsAppSwitcherProps> = ({ collapsed = false }) => {
  const { t } = useTranslation('pages')
  const [session, setSession] = React.useState<SessionResponse | null>(null)

  React.useEffect(() => {
    const controller = new AbortController()

    async function loadSession() {
      const authUrl = getConfig('VITE_AUTH_URL') ?? getConfig('VITE_AUTH_SERVICE_URL', 'http://localhost:8080')
      if (!authUrl) return

      try {
        const response = await fetch(`${authUrl.replace(/\/$/, '')}/session`, {
          credentials: 'include',
          signal: controller.signal,
        })
        if (!response.ok) return

        const session = await response.json() as SessionResponse
        if (session.authenticated) {
          setSession(session)
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('Failed to load analytics session:', error)
        }
      }
    }

    void loadSession()
    return () => controller.abort()
  }, [])

  if (!isPlatformAdmin(session)) return null

  const appLabels: Record<AgionAppId, string> = {
    admin: t('appSwitcher.admin', { defaultValue: 'Admin' }),
    control: t('appSwitcher.control', { defaultValue: 'Control' }),
    app: t('appSwitcher.app', { defaultValue: 'App' }),
    analytics: t('appSwitcher.analytics', { defaultValue: 'Analytics' }),
  }

  return (
    <AppSwitcher
      apps={createAgionAppSwitcherApps(appLabels)}
      currentAppId={getCurrentAgionAppId() ?? undefined}
      variant="sidebar"
      collapsed={collapsed}
      labels={{
        selectApp: t('appSwitcher.selectApp', { defaultValue: 'Select App' }),
        title: t('appSwitcher.title', { defaultValue: 'Applications' }),
        description: t('appSwitcher.description', { defaultValue: 'Switch between Agion applications' }),
        tooltip: t('appSwitcher.tooltip', { defaultValue: 'Switch application' }),
      }}
    />
  )
}
